import { useEffect, useMemo, useRef, useState } from "react";
import { Search, X, Check, UserPlus, ChevronDown } from "lucide-react";
import api from "../lib/apiClient";

/**
 * Searchable customer combobox. Drop-in replacement for the plain <select> used everywhere
 * before. Supports keyboard navigation, free-text filter on name / company / email / phone /
 * address, and a "create new customer" shortcut.
 *
 * Props:
 *   value          (string)        currently selected customer_id ("" = none)
 *   onChange       (id, customer)  fires whenever the user picks a customer
 *   required       (bool)
 *   placeholder    (string)
 *   testid         (string)        data-testid for the trigger button
 *   allowCreate    (bool)          show "+ New customer" footer link
 *   onCreateNew    (string|null)   handler — receives current search text
 *   customers      (Array)         optional pre-loaded list; otherwise self-loaded
 */
export default function CustomerPicker({
  value, onChange, required, placeholder = "Search customers…",
  testid = "customer-picker", allowCreate = false, onCreateNew, customers: passed,
}) {
  const [customers, setCustomers] = useState(passed || []);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const wrapRef = useRef();
  const inputRef = useRef();

  useEffect(() => {
    if (passed) { setCustomers(passed); return; }
    api.get("/customers").then(({ data }) => setCustomers(data)).catch(() => {});
  }, [passed]);

  useEffect(() => {
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 30);
  }, [open]);

  const selected = useMemo(
    () => customers.find((c) => c.id === value) || null,
    [customers, value]
  );

  const filtered = useMemo(() => {
    if (!q) return customers.slice(0, 50);
    const s = q.toLowerCase();
    return customers.filter((c) =>
      (c.name || "").toLowerCase().includes(s) ||
      (c.company_name || "").toLowerCase().includes(s) ||
      (c.email || "").toLowerCase().includes(s) ||
      (c.phone || "").toLowerCase().includes(s) ||
      (c.billing_address || "").toLowerCase().includes(s) ||
      (c.service_addresses || []).join(" ").toLowerCase().includes(s)
    ).slice(0, 50);
  }, [customers, q]);

  return (
    <div ref={wrapRef} className="relative">
      {selected && !open ? (
        // Selected state — clickable pill showing the picked customer
        <button
          type="button"
          data-testid={testid}
          onClick={() => { setOpen(true); setQ(""); setTimeout(() => inputRef.current?.focus(), 30); }}
          aria-required={required}
          className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81] bg-white text-left flex items-center justify-between gap-2"
        >
          <span className="text-slate-900 truncate">
            {selected.name}
            {selected.company_name && <span className="text-slate-500"> · {selected.company_name}</span>}
          </span>
          <ChevronDown size={14} className="text-slate-400 shrink-0" />
        </button>
      ) : (
        // Combobox: single text input that opens the dropdown on focus and filters live
        <div className="relative">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            ref={inputRef}
            data-testid={testid}
            type="text"
            value={q}
            placeholder={placeholder || "Search by name, email, phone, address…"}
            aria-required={required}
            onFocus={() => setOpen(true)}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            className="w-full pl-8 pr-8 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81] bg-white"
          />
          {q && (
            <button
              type="button"
              onClick={() => { setQ(""); inputRef.current?.focus(); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
              aria-label="Clear search"
            >
              <X size={13} />
            </button>
          )}
        </div>
      )}

      {open && (
        <div data-testid={`${testid}-dropdown`} className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-md shadow-xl max-h-80 overflow-hidden flex flex-col">
          <ul className="flex-1 overflow-y-auto divide-y divide-slate-100">
            {filtered.length === 0 && (
              <li className="px-4 py-6 text-center text-sm text-slate-400">No matches.</li>
            )}
            {filtered.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  onClick={() => { onChange(c.id, c); setOpen(false); setQ(""); }}
                  data-testid={`${testid}-option-${c.id}`}
                  className={`w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center justify-between gap-2 ${c.id === value ? "bg-blue-50" : ""}`}
                >
                  <div className="min-w-0">
                    <div className="font-medium text-sm text-slate-900 truncate">
                      {c.name}{c.company_name && <span className="text-slate-500 font-normal"> · {c.company_name}</span>}
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      {[c.email, c.phone, c.billing_address].filter(Boolean).join(" · ") || "—"}
                    </div>
                  </div>
                  {c.id === value && <Check size={14} className="text-[#0F4C81] shrink-0" />}
                </button>
              </li>
            ))}
          </ul>
          {allowCreate && (
            <button
              type="button"
              onClick={() => { setOpen(false); onCreateNew?.(q); }}
              data-testid={`${testid}-create-new`}
              className="px-3 py-2 border-t border-slate-200 text-sm text-[#0F4C81] hover:bg-slate-50 flex items-center gap-2 font-medium"
            >
              <UserPlus size={13} /> Create new customer{q ? ` "${q}"` : ""}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
